import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { NAV_LINKS } from "@/data/siteData";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        className={`navbar-glass fixed top-0 left-0 right-0 z-50 ${scrolled ? "scrolled" : ""}`}
        data-testid="navbar"
      >
        <div className="max-w-8xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src="/icon.png" alt="Uproot Innovation" className="h-12 w-12 object-contain" />
            <span className="font-['Outfit'] font-bold text-lg text-[#0A0A0A] hidden sm:block">
              Uproot <span className="text-[#097DC3]">Innovation</span>
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const isContactUs = link.label === "Contact Us";
              const isActive = location.pathname === link.path;
              
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    isContactUs
                      ? `border-2 ${
                          isActive
                            ? "border-[#097DC3] text-white bg-[#097DC3]"
                            : "border-[#097DC3] text-white bg-[#097DC3] hover:bg-transparent hover:text-[#097DC3]"
                        }`
                      : isActive
                      ? "text-[#097DC3] bg-[#097DC3]/8"
                      : "text-[#475569] hover:text-[#097DC3] hover:bg-[#097DC3]/5"
                  }`}
                  data-testid={`nav-link-${link.path.replace(/\//g, "") || "home"}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 mobile-menu-overlay lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-2xl lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-['Outfit'] font-bold text-lg">
                    Uproot <span className="text-[#097DC3]">Innovation</span>
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {NAV_LINKS.map((link, i) => {
                    const isContactUs = link.label === "Contact Us";
                    const isActive = location.pathname === link.path;
                    
                    return (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <Link
                          to={link.path}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                            isContactUs
                              ? `border-2 ${
                                  isActive
                                    ? "border-[#097DC3] text-white bg-[#097DC3]"
                                    : "border-[#097DC3] text-white bg-[#097DC3] hover:bg-transparent hover:text-[#097DC3]"
                                }`
                              : isActive
                              ? "text-[#097DC3] bg-[#097DC3]/8"
                              : "text-[#475569] hover:text-[#097DC3] hover:bg-slate-50"
                          }`}
                          data-testid={`mobile-nav-${link.path.replace(/\//g, "") || "home"}`}
                        >
                          {link.label}
                          <ChevronRight size={16} className="opacity-40" />
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
